import React from "react";
import { MdCardGiftcard, MdOutlineElectricBolt, MdSportsEsports } from "react-icons/md";
import { HiPhone } from "react-icons/hi";
import { pendingClock } from "../../assets/images";
import type { ActivityItem } from "../../utils/activity/normalizeTransaction";
import { resolveActivityIcon } from "../../utils/activity/resolveActivityIcon";

/**
 * Row avatar for a normalized activity item.
 *
 * Keyed off `item.iconMeta` rather than `transactionType` — that indirection is
 * the whole point of normalizing, and it's what lets one component serve the
 * activity page, the dashboard recents and the wallet coin list.
 */
const ActivityItemIcon = ({
  item,
  giftCardImage,
}: {
  item: ActivityItem;
  giftCardImage?: string;
}) => {
  const wrapper =
    "flex justify-center items-center w-[24px] h-[24px] rounded-full shrink-0 overflow-hidden";

  if (item.status === "pending") {
    return (
      <div className={wrapper}>
        <img src={pendingClock} className="w-full h-full bg-cover" alt="" />
      </div>
    );
  }

  if (item.type === "giftcard") {
    return (
      <div className={wrapper}>
        {giftCardImage ? (
          <img src={giftCardImage} className="w-full h-full object-cover" alt="" />
        ) : (
          <MdCardGiftcard className="text-[20px] text-[#03C668]" />
        )}
      </div>
    );
  }

  const src = resolveActivityIcon(item.iconMeta);
  if (src) {
    return (
      <div className={wrapper}>
        <img src={src} className="w-full h-full bg-cover" alt="" />
      </div>
    );
  }

  // Nothing matched the lookup tables — fall back by category.
  return (
    <div className={wrapper}>
      {item.iconMeta.kind === "utility" ? (
        item.iconMeta.isElectricity ? (
          <MdOutlineElectricBolt className="text-[20px] text-[#CB9200]" />
        ) : item.subtype === "betting" ? (
          <MdSportsEsports className="text-[20px] text-[#60B7F6]" />
        ) : (
          <HiPhone className="text-[18px] text-[#60B7F6]" />
        )
      ) : (
        // Coin/bank with no logo on file: a lettered disc beats a broken image.
        <span className="w-full h-full flex justify-center items-center bg-[#E8F3FC] dark:bg-[#2A3844] text-[10px] font-bold text-[#042741] dark:text-gray-200">
          {item.iconMeta.value?.slice(0, 2) || "?"}
        </span>
      )}
    </div>
  );
};

export default React.memo(ActivityItemIcon);
